// File: src/components/ImageViewerComponent.js
import React, { useState, useEffect } from 'react';
import './ImageViewerComponent.css';
import './transition/ImageViewerTransition.css';
import DemoPage from '../pdfviewer/pages/DemoPage';
import useImageLoader from '../pdfviewer/utils/imageLoader';

// Impor gambar PNG dari local path
import prevIcon from '../assets/icon/prev-carousel.png'; 
import nextIcon from '../assets/icon/next-carousel.png'; 

const ImageViewerComponent = ({ 
  folderName, 
  pageLimit, 
  isLazyLoadEnabled = true,
  isAnimated = true,
  openPurchaseLink
}) => {
  
  const { imagePaths, loading, error } = useImageLoader(folderName);
  
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0); 
  const [isTwoPageMode, setIsTwoPageMode] = useState(false); 
  const [enableTransitions] = useState(true);
  const [loadedImages, setLoadedImages] = useState({});

  const actualTotalImageCount = imagePaths ? imagePaths.length : 0;
  const TWO_PAGE_BREAKPOINT = 992;

  useEffect(() => {
    const handleResize = () => {
      setIsTwoPageMode(window.innerWidth >= TWO_PAGE_BREAKPOINT);
    };
    window.addEventListener('resize', handleResize);
    handleResize(); 
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (imagePaths) {
      const effectiveTotalImages = pageLimit ? Math.min(pageLimit, actualTotalImageCount) : actualTotalImageCount;
      let calculatedTotalDisplayPages; 
      if (isTwoPageMode) {
        calculatedTotalDisplayPages = Math.ceil(effectiveTotalImages / 2); 
      } else {
        calculatedTotalDisplayPages = effectiveTotalImages;
      }
      setTotalPages(calculatedTotalDisplayPages);
    } else {
      setTotalPages(0);
    }
    setCurrentPage(1);
  }, [imagePaths, pageLimit, isTwoPageMode, actualTotalImageCount]);

  useEffect(() => {
    const loadImage = (pageNumber) => {
      if (
        imagePaths && 
        imagePaths[pageNumber - 1] && 
        (!pageLimit || pageNumber <= pageLimit) &&
        !loadedImages[pageNumber]
      ) {
        const img = new Image();
        img.src = imagePaths[pageNumber - 1];
        img.onload = () => {
          setLoadedImages(prevImages => ({
            ...prevImages,
            [pageNumber]: imagePaths[pageNumber - 1]
          }));
        };
      }
    };

    if (isLazyLoadEnabled) {
      const firstContentPageToLoad = (currentPage - 1) * (isTwoPageMode ? 2 : 1) + 1;
      if (!pageLimit || firstContentPageToLoad <= pageLimit) {
        loadImage(firstContentPageToLoad);
      }
      if (isTwoPageMode) {
        const secondContentPageToLoad = firstContentPageToLoad + 1;
        if (!pageLimit || secondContentPageToLoad <= pageLimit) {
          loadImage(secondContentPageToLoad);
        }
      }
    } else {
      if (imagePaths) {
        const allImages = imagePaths.reduce((acc, url, index) => {
          if (!pageLimit || (index + 1) <= pageLimit) {
            acc[index + 1] = url;
          }
          return acc;
        }, {});
        setLoadedImages(allImages);
      }
    }
  }, [isLazyLoadEnabled, currentPage, isTwoPageMode, imagePaths, pageLimit, totalPages, loadedImages]);

  const handlePrev = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => Math.max(1, prev - 1));
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const isCurrentlyShowingFullDemoPage = pageLimit && currentPage === totalPages;

  const handleImageClick = (e) => {
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const width = rect.width;
  
    if (isCurrentlyShowingFullDemoPage) {
        if (x < width / 2) {
          handlePrev();
        }
        return; 
    }

    if (!isTwoPageMode) {
      if (x < width / 2) { 
        if (currentPage > 1) {
          handlePrev();
        }
      } else { 
        if (currentPage < totalPages) {
          handleNext();
        }
      }
    } else {
      const contentSlots = [];
      const firstImageActualIndex = (currentPage - 1) * (isTwoPageMode ? 2 : 1);
      const determineSlotContent = (slotIndex) => {
        const pageNumber = firstImageActualIndex + slotIndex + 1;
        if (pageNumber <= (pageLimit || actualTotalImageCount)) {
          return { type: 'image' };
        }
        return { type: 'empty' };
      };

      contentSlots.push(determineSlotContent(0));
      if (isTwoPageMode) {
        contentSlots.push(determineSlotContent(1));
      }
      
      const clickedImageIndex = Math.floor((e.clientX - rect.left) / (width / contentSlots.length));
      
      if (contentSlots.length === 1 || (contentSlots.length === 2 && contentSlots[0].type === 'empty')) {
        if (x < width / 2) {
          if (currentPage > 1) {
            handlePrev();
          }
        } else {
          if (currentPage < totalPages) {
            handleNext();
          }
        }
      } else {
        if (clickedImageIndex === 0) {
          if (currentPage > 1) {
            handlePrev();
          }
        } else if (clickedImageIndex === 1) {
          if (currentPage < totalPages) {
            handleNext();
          }
        }
      }
    }
  };
  
  if (loading) {
    return <div className="loading-state">Memuat folder...</div>;
  }

  if (error) {
    return (
      <div className="image-viewer-container">
        <div className="error-state">
          <span>{error}</span>
        </div>
      </div>
    );
  }

  const contentSlots = []; 
  const firstImageActualIndex = (currentPage - 1) * (isTwoPageMode ? 2 : 1);

  const determineSlotContent = (slotIndex) => {
    const pageNumber = firstImageActualIndex + slotIndex + 1;
    
    if (pageNumber > (pageLimit || actualTotalImageCount)) {
      return { type: 'empty', key: `empty-slot-${slotIndex}` };
    }

    if (imagePaths && loadedImages[pageNumber]) {
      return { type: 'image', url: loadedImages[pageNumber], pageNum: pageNumber, key: `img-slot-${pageNumber}` };
    } else {
      return { type: 'loading', key: `loading-slot-${slotIndex}` };
    }
  };

  if (!isCurrentlyShowingFullDemoPage) {
    contentSlots.push(determineSlotContent(0));
    if (isTwoPageMode) {
      contentSlots.push(determineSlotContent(1));
    }
  }

  const displayedEffectiveTotalImages = pageLimit ? Math.min(pageLimit, actualTotalImageCount) : actualTotalImageCount;
  
  let displayedPageRangeText;
  if (isCurrentlyShowingFullDemoPage) {
    displayedPageRangeText = `Halaman Demo (Halaman ${currentPage} dari ${totalPages})`;
  } else {
    let startContentPage = (currentPage - 1) * (isTwoPageMode ? 2 : 1) + 1;
    let endContentPage = startContentPage;
    if (isTwoPageMode) {
      endContentPage = Math.min(startContentPage + 1, displayedEffectiveTotalImages);
    }
    if (isTwoPageMode && contentSlots.length === 2 && contentSlots[0].type === 'image' && contentSlots[1].type === 'empty') {
      endContentPage = startContentPage;
    }
    if (isTwoPageMode && startContentPage === endContentPage && displayedEffectiveTotalImages >= startContentPage) {
      displayedPageRangeText = `Halaman ${startContentPage} dari ${displayedEffectiveTotalImages}`;
    } else if (isTwoPageMode && displayedEffectiveTotalImages >= startContentPage) {
      displayedPageRangeText = `Halaman ${startContentPage}-${endContentPage} dari ${displayedEffectiveTotalImages}`;
    } else if (!isTwoPageMode && displayedEffectiveTotalImages >= currentPage) {
      displayedPageRangeText = `Halaman ${currentPage} dari ${displayedEffectiveTotalImages}`;
    } else {
      displayedPageRangeText = `Halaman ${currentPage} dari ${displayedEffectiveTotalImages}`;
    }
  }

  return (
    <div className={`image-viewer-container ${isAnimated ? 'transition-fade-in' : ''}`}>
      
      <div className="image-wrapper" onClick={handleImageClick}>
        {isCurrentlyShowingFullDemoPage ? (
          <DemoPage purchaseLink={openPurchaseLink}/>
        ) : (
          <div className={isTwoPageMode ? "two-page-display" : ""}>
            {contentSlots.map(slot => {
              if (slot.type === 'image') {
                return (
                  <div key={slot.key} className="aspect-ratio-box">
                    <img 
                      src={slot.url} 
                      alt={`Halaman ${slot.pageNum}`} 
                      className="document-image"
                      style={enableTransitions ? {} : { transition: 'none' }} 
                    />
                  </div>
                );
              } else if (slot.type === 'loading') {
                return (
                  <div key={slot.key} className="aspect-ratio-box">
                    <div className="loading-state">Memuat halaman...</div>
                  </div>
                );
              } else if (slot.type === 'empty') {
                return <div key={slot.key} className="empty-slot"></div>; 
              }
              return null;
            })}
          </div>
        )}
        
        <button 
          className={`nav-button prev-button ${currentPage === 1 ? 'disabled' : ''}`} 
          onClick={(e) => { e.stopPropagation(); handlePrev(); }}
          disabled={currentPage === 1}
        >
          <img src={prevIcon} alt="Previous Page" className="nav-icon" />
        </button>

        <button 
          className={`nav-button next-button ${currentPage >= totalPages ? 'disabled' : ''}`} 
          onClick={(e) => { e.stopPropagation(); handleNext(); }}
          disabled={currentPage >= totalPages}
        >
          <img src={nextIcon} alt="Next Page" className="nav-icon" />
        </button>
      </div>
      
      <div className="status-bar">
        <span>{displayedPageRangeText}</span>
      </div>
    </div>
  );
};

export default ImageViewerComponent;